import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { replaceOwnAvatar } from "../../../api/avatarsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import {
  displayScale,
  initialFrame,
  MAXIMUM_ZOOM,
  MINIMUM_ZOOM,
  movedFrame,
  sourceSquare,
  zoomedFrame,
  type CropFrame,
  type PictureSize,
} from "./squareCrop";

/** The editor's square, in CSS pixels. */
const VIEWPORT = 224;
/** The uploaded square: sharp at every avatar size, far below the server's limit. */
const OUTPUT_SIZE = 512;
const KEY_STEP = 8;

type Loaded = { readonly status: "loading" } | { readonly status: "failed" } | { readonly status: "ready"; readonly image: HTMLImageElement; readonly size: PictureSize };

/**
 * Choose the part of a picture to keep (F14), inline on the Profile page: the picture covers
 * a square with a round mask; drag it, or use the arrow keys, and zoom with the slider. The
 * browser crops and scales the square, then uploads it.
 */
export default function AvatarCropEditor({ file, onSaved, onCancel }: { file: File; onSaved: (version: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const loaded = useDecodedPicture(file);
  const [frame, setFrame] = useState<CropFrame | null>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);
  const saving = useSubmission();

  if (loaded.status === "loading") {
    return <p className="app-text-muted text-sm" role="status">{t("profile.avatar.loading")}</p>;
  }
  if (loaded.status === "failed") {
    return (
      <div className="space-y-2">
        <StatusMessage tone="error">{t("profile.avatar.unreadable")}</StatusMessage>
        <AppButton size="sm" onClick={onCancel}>{t("profile.avatar.cancel")}</AppButton>
      </div>
    );
  }
  const { image, size } = loaded;
  const current = frame ?? initialFrame(size, VIEWPORT);
  const scale = displayScale(size, VIEWPORT, current.zoom);
  const move = (deltaX: number, deltaY: number) => {
    setFrame(movedFrame(current, size, VIEWPORT, deltaX, deltaY));
  };
  const save = () => {
    saving.submit(async () => {
      onSaved(await replaceOwnAvatar(await croppedSquare(image, current, size)));
    });
  };

  return (
    <div className="space-y-3">
      <div
        className="app-avatar-crop"
        style={{ width: VIEWPORT, height: VIEWPORT }}
        tabIndex={0}
        role="img"
        aria-label={t("profile.avatar.cropArea")}
        onPointerDown={(event: PointerEvent<HTMLDivElement>) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerMove={(event: PointerEvent<HTMLDivElement>) => {
          if (drag.current === null) return;
          move(event.clientX - drag.current.x, event.clientY - drag.current.y);
          drag.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={() => { drag.current = null; }}
        onPointerCancel={() => { drag.current = null; }}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          const step = ARROW_STEPS[event.key];
          if (step === undefined) return;
          event.preventDefault();
          move(step[0] * KEY_STEP, step[1] * KEY_STEP);
        }}
      >
        <img
          src={image.src}
          alt=""
          draggable={false}
          className="app-avatar-crop-picture"
          style={{ left: current.left, top: current.top, width: size.width * scale, height: size.height * scale }}
        />
      </div>
      <Field label={t("profile.avatar.zoom")} layout="inline">
        {(control) => (
          <input
            {...control}
            type="range"
            min={MINIMUM_ZOOM}
            max={MAXIMUM_ZOOM}
            step={0.01}
            value={current.zoom}
            onChange={(event) => { setFrame(zoomedFrame(current, size, VIEWPORT, Number(event.target.value))); }}
          />
        )}
      </Field>
      <p className="app-help-text">{t("profile.avatar.cropHint")}</p>
      {saving.state.status === "failed" ? <ErrorNotice error={saving.state.error} compact /> : null}
      <div className="flex gap-2">
        <AppButton size="sm" variant="primary" onClick={save} disabled={saving.state.status === "submitting"}>{t("profile.avatar.save")}</AppButton>
        <AppButton size="sm" onClick={onCancel} disabled={saving.state.status === "submitting"}>{t("profile.avatar.cancel")}</AppButton>
      </div>
    </div>
  );
}

const ARROW_STEPS: Readonly<Partial<Record<string, readonly [number, number]>>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** The chosen file, decoded by the browser; any format it can show will do. The editor is mounted per file. */
function useDecodedPicture(file: File): Loaded {
  const [loaded, setLoaded] = useState<Loaded>({ status: "loading" });
  useEffect(() => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    let current = true;
    image.src = url;
    image.decode().then(
      () => { if (current) setLoaded({ status: "ready", image, size: { width: image.naturalWidth, height: image.naturalHeight } }); },
      () => { if (current) setLoaded({ status: "failed" }); },
    );
    return () => {
      current = false;
      URL.revokeObjectURL(url);
    };
  }, [file]);
  return loaded;
}

/** The kept square, scaled to `OUTPUT_SIZE`, as WebP where the browser writes it and PNG otherwise. */
function croppedSquare(image: HTMLImageElement, frame: CropFrame, size: PictureSize): Promise<Blob> {
  const square = sourceSquare(frame, size, VIEWPORT);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const context = canvas.getContext("2d");
  if (context === null) {
    return Promise.reject(new Error("This browser cannot draw the picture."));
  }
  context.imageSmoothingQuality = "high";
  context.drawImage(image, square.left, square.top, square.size, square.size, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => { if (blob === null) reject(new Error("This browser cannot encode the picture.")); else resolve(blob); }, "image/webp", 0.9);
  });
}
